import React from "react"
import { useLocation } from "react-router-dom"
import clsx from "clsx"

import { useTranslation } from "react-i18next"

import glowBg from "../../assets/images/glowBg.png"
import logo from "../../assets/images/logoWithText.png"
import logoLight from "../../assets/images/logoWithTextLight.png"
import { usePermissions } from "../../contexts/PermissionsContext"
import { useProps } from "../../contexts/PropsContext"

import { NAV_ITEMS, NavItem } from "./navConfig"
import { SidebarGroup } from "./SidebarGroup"
import { SidebarLink } from "./SidebarLink"
import { SidebarThemeToggle } from "./SidebarThemeToggle"
import { SidebarToggleButton } from "./SidebarToggleButton"

import "css/scrollbar.css"

interface Props {
  open: boolean
  setOpen: (open: boolean) => void
  children: React.ReactNode
}

export const Sidebar = ({ open, setOpen, children }: Props) => {
  const { t } = useTranslation("translation", { useSuspense: false })
  const { authenticated, kycFilesCount, user, submittedWithdrawalsCount, pendingReviewCount, darkMode, onDarkModeChange, bonusCatalog } =
    useProps()
  const { has } = usePermissions()
  const location = useLocation()

  const [collapsed, setCollapsed] = React.useState(false)
  const [expanded, setExpanded] = React.useState<Record<string, boolean>>({
    players: true,
    payments: true,
    marketing: false,
    content: false,
    system: false
  })

  const toggleGroup = (key: string) => {
    setExpanded((prev) => ({ ...prev, [key]: !prev[key] }))
  }

  const isActiveUrl = (url: string) => location.pathname === url

  const getBadgeValue = (badge?: string) => {
    if (!badge) return undefined
    if (badge === "withdrawals") return submittedWithdrawalsCount
    if (badge === "kyc") return kycFilesCount
    if (badge === "review") return pendingReviewCount
    return undefined
  }

  const navItems: NavItem[] = React.useMemo(() => {
    if (!user) return []

    // D01: which bonus catalog page to show — `bonusCatalog` comes from `/admin/tenant-mode`,
    // resolved before the app renders. Unresolved (fetch failed) = legacy, never both pages.
    const effectiveBonusMode = bonusCatalog ?? "legacy"

    const visible = (item?: { permission?: string; bonusCatalogGate?: "legacy" | "v2" }) =>
      (!item?.permission || has(item.permission as any)) &&
      (!item?.bonusCatalogGate || item.bonusCatalogGate === effectiveBonusMode)

    return NAV_ITEMS.filter((item) => visible(item))
      .map((item) => {
        if (item.type !== "group") return item
        return {
          ...item,
          items: item.items.filter((child) => visible(child))
        }
      })
      .filter((item) => item.type !== "group" || item.items.length > 0)
  }, [user, has, bonusCatalog])

  const navItemClass = (url: string, opts?: { compact?: boolean }) => {
    const isActive = location.pathname === url

    return [
      "flex items-center rounded-md transition text-sm font-medium border",
      opts?.compact ? "gap-3 px-3 py-1.5" : collapsed ? "justify-center px-2 py-2" : "gap-3 px-3 py-2",
      isActive
        ? darkMode
          ? "bg-[radial-gradient(50%_50%_at_50%_50%,_rgba(132,_0,_168,_0.4)_11%,_rgba(132,_0,_168,_0.05)_100%)] border-[rgba(255,255,255,0.1)] text-white"
          : "bg-[rgba(132,_0,_168,_1)] border-[rgba(132,_0,_168,_0.15)] text-[rgba(225,_239,_254,1)]"
        : darkMode
        ? "border-transparent hover:bg-[radial-gradient(50%_50%_at_50%_50%,_rgba(132,_0,_168,_0.15)_11%,_rgba(132,_0,_168,_0.04)_100%)] hover:border-[rgba(255,255,255,0.05)] text-neutral-300"
        : "border-transparent hover:bg-[rgba(132,_0,_168,_0.08)] hover:border-[rgba(132,_0,_168,_0.1)] text-[rgba(93,_96,_101,1)]"
    ].join(" ")
  }

  const groupHeaderClass = (isActive: boolean) => {
    return [
      "w-full flex items-center rounded-md transition text-sm font-medium border",
      collapsed ? "justify-center px-2 py-2" : "gap-3 px-3 py-2",
      isActive
        ? darkMode
          ? "bg-[radial-gradient(50%_50%_at_50%_50%,_rgba(132,_0,_168,_0.25)_11%,_rgba(132,_0,_168,_0.05)_100%)] border-[rgba(255,255,255,0.08)] text-white"
          : "bg-[rgba(132,_0,_168,_0.9)] border-[rgba(132,_0,_168,_0.15)] text-[rgba(225,_239,_254,1)]"
        : darkMode
        ? "border-transparent hover:bg-[radial-gradient(50%_50%_at_50%_50%,_rgba(132,_0,_168,_0.12)_11%,_rgba(132,_0,_168,_0.04)_100%)] hover:border-[rgba(255,255,255,0.05)] text-neutral-300"
        : "border-transparent hover:bg-[rgba(132,_0,_168,_0.08)] hover:border-[rgba(132,_0,_168,_0.1)] text-[rgba(93,_96,_101,1)]"
    ].join(" ")
  }

  const sidebarWidth = collapsed ? "lg:w-[72px]" : "lg:w-[260px]"
  const contentLeftPadding = authenticated ? (collapsed ? "lg:pl-[72px]" : "lg:pl-[260px]") : "lg:pl-[0px]"

  const onNavigate = () => setOpen(false)

  return (
    <div className={`flex h-auto min-h-[100vh] text-white ${darkMode ? "bg-[#060715]" : "bg-white"}`}>
      {darkMode && (
        <img
          src={glowBg}
          alt="background-glow"
          className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-full h-full opacity-60 pointer-events-none select-none"
        />
      )}

      {authenticated && open && <div className="fixed inset-0 z-40 bg-black/50 lg:hidden" onClick={() => setOpen(false)} />}

      {authenticated && (
        <div
          className={clsx(
            "fixed top-0 left-0 w-[280px] h-screen",
            "lg:top-auto lg:left-auto lg:h-[calc(100vh-32px)]",
            "flex flex-col justify-between py-6 md:px-4 lg:ml-4 lg:mt-6 rounded-lg border border-[rgba(255,255,255,0.1)] transition-[width] duration-200 ease-in-out",
            darkMode
              ? "bg-[#131421]"
              : "bg-white border-[rgba(233,234,236,1)] backdrop-blur-[9px] shadow-[0px_3px_6px_0px_rgba(192,192,192,0.4)]",
            open ? "flex z-50" : "hidden",
            "lg:flex",
            sidebarWidth
          )}
        >
          <div className="flex-1 min-h-0">
            <div className="thin-scrollbar overflow-y-auto overflow-x-hidden pr-2 h-full w-full lg:w-auto pb-24">
              <div>
                <div className={clsx("flex items-center mb-8", collapsed ? "justify-center" : "justify-between")}>
                  <SidebarToggleButton collapsed={collapsed} onToggle={() => setCollapsed((v) => !v)} darkMode={darkMode} />

                  {!collapsed && (
                    <div className="flex justify-center">
                      <img src={darkMode ? logo : logoLight} alt="SoftVault" className="w-[216px]" />
                    </div>
                  )}
                </div>

                <nav className="flex flex-col gap-1">
                  {navItems.map((item) => {
                    if (item.type === "link") {
                      const label = t(item.label)
                      const badgeValue = getBadgeValue(item.badge)

                      return (
                        <SidebarLink
                          key={item.url}
                          item={item}
                          label={label}
                          collapsed={collapsed}
                          badgeValue={badgeValue}
                          onNavigate={onNavigate}
                          className={navItemClass(item.url)}
                        />
                      )
                    }

                    return (
                      <SidebarGroup
                        key={item.key}
                        group={item}
                        collapsed={collapsed}
                        darkMode={darkMode}
                        expanded={!!expanded[item.key]}
                        onToggle={() => toggleGroup(item.key)}
                        tLabel={(s) => t(s)}
                        getBadgeValue={getBadgeValue}
                        navItemClass={navItemClass}
                        groupHeaderClass={groupHeaderClass}
                        isActiveUrl={isActiveUrl}
                        onNavigate={onNavigate}
                      />
                    )
                  })}
                </nav>
              </div>
            </div>
          </div>

          <SidebarThemeToggle darkMode={darkMode} collapsed={collapsed} onDarkModeChange={onDarkModeChange} />
        </div>
      )}

      {/*
        `min-w-0` is what lets a page scroll its own wide table instead of scrolling the
        whole window: without it this flex item keeps `min-width: auto`, grows to its
        widest child, and pushes the viewport sideways past the sidebar.
      */}
      <div className={clsx("min-w-0 flex-1 pl-[0px]", contentLeftPadding, { "pl-[260px]": open }, { "lg:pl-[0px]": !authenticated })}>
        {children}
      </div>
    </div>
  )
}
